import { isValidEmail } from "@shared/mirage/validators/EmailValidator";
import {
    IPrePpWlForm,
    PrePpWlDescriptionOfDevelopmentDevelopmentTypes,
    PrePpWlNonResidentialChangeOfUseTypes,
    PrePpWlTypeOfBuildDevelopmentTypes,
    PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes,
} from "../contracts/IPrePpWlForm";

export class PrPpWlFormManager {
    private static requiredMessage = "Complete this field";

    public static isFormValid(record: IPrePpWlForm): boolean {
        return Object.values(record).every((field) => !field.errorMsg);
    }
    public static customValidate(record: IPrePpWlForm): IPrePpWlForm {
        this.validateApplicantDetailsStep(record);
        this.validateDescriptionOfDevelopmentStep(record);
        this.validateTypeOfDevelopmentStep(record);
        this.validateSiteDetails(record);
        this.validateStageStep(record);
        this.validateAdditionalInformationStep(record);
        return record;
    }
    public static resetErrorMsg(record: IPrePpWlForm) {
        const keys = Object.keys(record);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i] as keyof IPrePpWlForm;
            if (typeof record[key] === "object") {
                record[key].errorMsg = "";
            }
        }
    }

    public static validateApplicantDetailsStep(record: IPrePpWlForm): IPrePpWlForm {
        this.resetErrorMsg(record);
        this.setRequiredField(record, "applicantFirstName");
        this.setRequiredField(record, "applicantSurname");
        this.setRequiredField(record, "applicantPhoneNumber");
        this.setRequiredField(record, "applicantPostcode");
        this.setRequiredField(record, "applicantAddress");
        this.setRequiredField(record, "applicantPostTown");
        this.validateEmail(record, "applicantEmail");
        return record;
    }
    public static validateAgentDetailsStep(record: IPrePpWlForm): IPrePpWlForm {
        this.resetErrorMsg(record);
        this.validateEmail(record, "agentEmail");
        return record;
    }
    public static validateDescriptionOfDevelopmentStep(record: IPrePpWlForm): IPrePpWlForm {
        this.resetErrorMsg(record);
        this.setRequiredField(record, "typeOfDevelopment");
        return record;
    }

    public static validateTypeOfDevelopmentStep(record: IPrePpWlForm): IPrePpWlForm {
        this.resetErrorMsg(record);
        this.setRequiredField(record, "developmentScale");
        this.setRequiredField(record, "totalNumberOfUnitsProposed");
        this.setRequiredField(record, "typeOfNewBuild");
        this.setRequiredField(record, "additionalGrossFloorSpace");
        this.setRequiredField(record, "nonResidentialChangeOfUse");
        return record;
    }
    public static validateSiteDetails(record: IPrePpWlForm): IPrePpWlForm {
        this.resetErrorMsg(record);
        this.setRequiredField(record, "currentUseOfTheSite");
        this.setRequiredField(record, "siteLocationAddress");
        this.setRequiredField(record, "siteLocationPostTown");
        this.setRequiredField(record, "siteLocationPostcode");
        return record;
    }
    public static validateStageStep(record: IPrePpWlForm): IPrePpWlForm {
        this.resetErrorMsg(record);
        this.setRequiredField(record, "enquiryStage");
        return record;
    }
    public static validateAdditionalInformationStep(record: IPrePpWlForm): IPrePpWlForm {
        this.resetErrorMsg(record);
        this.essentialDocumentsRequired(record);
        this.optionalDocumentsRequired(record);
        return record;
    }

    public static essentialDocumentsRequired(record: IPrePpWlForm): IPrePpWlForm {
        this.resetErrorMsg(record);
        this.setRequiredField(record, "siteLocationDocuments");
        return record;
    }
    public static optionalDocumentsRequired(record: IPrePpWlForm): IPrePpWlForm {
        this.resetErrorMsg(record);
        this.setRequiredField(record, "optionalDocuments");
        return record;
    }
    public static validateEmail(record: IPrePpWlForm, fieldName: "applicantEmail" | "agentEmail") {
        if (!record[fieldName].value) {
            return;
        }
        const emailValue = record[fieldName].value;
        const emailIsValid = isValidEmail(emailValue);
        if (!emailIsValid) {
            record[fieldName].errorMsg = "Enter a valid email";
        } else {
            record[fieldName].errorMsg = "";
        }
    }

    private static setRequiredField(record: IPrePpWlForm, fieldName: keyof IPrePpWlForm) {
        const isRequired = this.isFieldRequired(record, fieldName);
        if (!isRequired) {
            this.clearErrorMsg(record, fieldName);
            return;
        }
        const value = record[fieldName].value;
        if (typeof value === "string" && !value.trim().length) {
            record[fieldName].errorMsg = this.requiredMessage;
        } else if (Array.isArray(value) && !value.length) {
            record[fieldName].errorMsg = this.requiredMessage;
        } else {
            this.clearErrorMsg(record, fieldName);
        }
    }
    private static clearErrorMsg(record: IPrePpWlForm, fieldName: keyof IPrePpWlForm) {
        record[fieldName].errorMsg = "";
    }

    public static isFieldRequired(record: IPrePpWlForm, fieldName: keyof IPrePpWlForm): boolean {
        switch (fieldName) {
            case "applicantFirstName":
            case "applicantSurname":
            case "applicantPhoneNumber":
            case "applicantPostTown":
            case "developmentScale":
            case "typeOfDevelopment":
            case "currentUseOfTheSite":
            case "enquiryStage":
                return true;
            case "applicantAddress": {
                return !record.applicantAddressLine1.value && !record.applicantAddressLine2.value && !record.applicantAddressLine3.value;
            }
            case "totalNumberOfUnitsProposed": {
                return record.typeOfDevelopment.value === PrePpWlDescriptionOfDevelopmentDevelopmentTypes.NEW_DWELLINGS;
            }
            case "typeOfNewBuild": {
                return (
                    record.typeOfDevelopment.value === PrePpWlDescriptionOfDevelopmentDevelopmentTypes.NEW_DWELLINGS &&
                    record.developmentScale.value === PrePpWlTypeOfBuildDevelopmentTypes.NEW_BUILD
                );
            }
            case "additionalGrossFloorSpace": {
                return (
                    record.typeOfDevelopment.value === PrePpWlDescriptionOfDevelopmentDevelopmentTypes.RETAIL_OFFICE_INDUSTRIAL &&
                    [PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.EXTENSION, PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.NEW_BUILD].includes(
                        record.developmentScale.value as any
                    )
                );
            }
            case "nonResidentialChangeOfUse": {
                return record.developmentScale.value === PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.CHANGE_OF_USE;
            }
            case "siteLocationPostTown": {
                return !record.siteLocationIsSameAsApplicant.value;
            }
            case "siteLocationAddress": {
                return !record.siteLocationAddressLine1.value && !record.siteLocationAddressLine2.value && !record.siteLocationAddressLine3.value;
            }
            case "siteLocationDocuments": {
                return !record.hardCopySiteLocationPlansWillBeProvided.value;
            }
            case "optionalDocuments": {
                return (
                    !record.hardCopyOptionalDocumentsWillBeProvided.value &&
                    (record.blockPlanProvided.value ||
                        record.existingDetailsProvided.value ||
                        record.proposedDetailsProvided.value ||
                        record.photographsProvided.value ||
                        record.designStatementProvided.value)
                );
            }
            default:
                return false;
        }
    }

    public static isAgentDetailsEmpty(record: IPrePpWlForm): boolean {
        return Object.entries(record).every(([key, value]) => {
            if (key.includes("agent")) {
                return !value.value;
            }
            return true;
        });
    }

    public static isAdditionalGrossFloorSpaceType(developmentScale?: string): developmentScale is AdditionalGrossFloorSpaceType {
        return [PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.EXTENSION, PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.NEW_BUILD].includes(developmentScale as any);
    }

    public static clearTypeOfDevelopmentFields(record: IPrePpWlForm) {
        record.developmentScale.value = "";

        record.alterationWidth.value = "";
        record.alterationLength.value = "";
        record.alterationHeight.value = "";
        record.alterationHeightToEaves.value = "";
        record.otherAlterationOrExtensionSpecified.value = "";

        record.totalNumberOfUnitsProposed.value = "";
        record.typeOfNewBuild.value = "";

        record.additionalGrossFloorSpace.value = "";
        record.additionalFullTimeEmployees.value = "";
        record.additionalPartTimeEmployees.value = "";
        record.estimatedNumberOfVehicularTrips.value = "";

        record.nonResidentialChangeOfUse.value = "" as typeof PrePpWlNonResidentialChangeOfUseTypes[keyof typeof PrePpWlNonResidentialChangeOfUseTypes];
        record.alterationsDescription.value = "";
        record.otherDevelopmentDescription.value = "";
    }
}

export type AdditionalGrossFloorSpaceType =
    | typeof PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.EXTENSION
    | typeof PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.NEW_BUILD;
